import { useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { incrementRequestsCount, decrementRequestsCount } from './AxiosConfigSlice';
import { useNavigate } from 'react-router-dom';
import { resetUserState, setTokens } from '../slicers/UserSlice';
import { showTimeOutAlert } from '../slicers/TimeOutAlertSlice';

const useAxios = (baseURL, isSpinner = false) => {
  const dispatch = useDispatch();
  const tokens = useSelector((state) => state.userInfo.tokens);
  const navigate = useNavigate();
  const tokensRef = useRef(tokens);
  const isSigningOutRef = useRef(false);

  const refreshPromiseRef = useRef(null);

  useEffect(() => {
    tokensRef.current = tokens;
  }, [tokens]);

  const axiosInstance = useMemo(() => {
    const instance = axios.create({ baseURL });

    instance.interceptors.request.use(
      (config) => {
        if (tokensRef.current) {
          // Check if the token already has "Bearer" prefix
          const authToken = tokensRef.current.authorization;
          const tokenWithBearer = authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`;
          config.headers['authorization'] = tokenWithBearer;
          config.headers['refresh'] = `${tokensRef.current.refresh}`;
          config.headers['id'] = `${tokensRef.current.id}`;
        }

        if (!isSpinner) {
          dispatch(incrementRequestsCount());
        }

        return config;
      },
      (error) => {
        if (!isSpinner) {
          dispatch(decrementRequestsCount());
        }
        return Promise.reject(error);
      }
    );

    instance.interceptors.response.use(
      (response) => {
        if (!isSpinner) {
          dispatch(decrementRequestsCount());
        }

        return response;
      },
      async (error) => {
        if (!isSpinner) {
          dispatch(decrementRequestsCount());
        }
        const responseInfo = error.response?.data;
        const originalRequest = error.config;

        if (responseInfo?.message === "Invalid token" && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            // If a refresh is already in progress, wait for it
            if (!refreshPromiseRef.current) {
              refreshPromiseRef.current = instance.post(
                '/refresh-token',
                {},
                {
                  headers: {
                    'Content-Type': 'application/json',
                    'refresh': tokensRef.current.refresh,
                    'authorization': tokensRef.current.authorization.startsWith('Bearer ') ? tokensRef.current.authorization : `Bearer ${tokensRef.current.authorization}`,
                    'id': `${tokensRef.current.id}`,
                  },
                }
              ).then((refreshResponse) => {
                const newTokens = {
                  refresh: tokensRef.current.refresh,
                  authorization: refreshResponse.data.data,
                  id: tokensRef.current.id,
                };
                dispatch(setTokens(newTokens));
                tokensRef.current = newTokens;
                return newTokens;
              }).finally(() => {
                refreshPromiseRef.current = null;
              });
            }
            // Wait for the refresh to complete
            const newTokens = await refreshPromiseRef.current;
            // Update the original request with new tokens
            originalRequest.headers['authorization'] = newTokens.authorization.startsWith('Bearer ') ? newTokens.authorization : `Bearer ${newTokens.authorization}`;
            originalRequest.headers['refresh'] = `${newTokens.refresh}`;
            originalRequest.headers['id'] = `${newTokens.id}`;
            // Retry the original request
            return instance(originalRequest);
          } catch (refreshError) {
            // If refresh fails, log out
            if (!isSigningOutRef.current) {
              isSigningOutRef.current = true;
    
              try {
                await instance.post('/signout');
              } catch (signoutError) {
                console.error('Failed to call signout:', signoutError);
              }
    
              dispatch(resetUserState());
              navigate('/');
              dispatch(showTimeOutAlert());
            }
    
            return Promise.reject(refreshError);
          }
        }
        if (responseInfo?.message === "Authentication failed. Please log in again.") {
          if (!isSigningOutRef.current) {
            isSigningOutRef.current = true;
    
            try {
              await instance.post('/signout');
            } catch (signoutError) {
              console.error('Failed to call signout:', signoutError);
            }
    
            dispatch(resetUserState());
            navigate('/');
            dispatch(showTimeOutAlert());
          }
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, [dispatch, baseURL, isSpinner, navigate]);

  return axiosInstance;
};

export default useAxios;
